import { isZero, useTokenBalancesData, useWallet } from "@solomon-apps/shared";
import { useEffect, useMemo, useRef } from "react";

import { useUserStakingStats } from "@/hooks/staking";

const STORAGE_KEY_LAST_ADDRESS = "solomon:lastWalletAddress";
const STORAGE_KEY_HAD_BALANCE_PREFIX = "solomon:hadBalance:";

/** Synchronously read the cached "had balance" answer for the most recently
 *  active wallet. Returns `null` when there's no cache so callers can tell
 *  "unknown" apart from "known false". */
function readCachedHadBalance(): boolean | null {
    if (typeof window === "undefined") return null;
    try {
        const lastAddress = window.localStorage.getItem(STORAGE_KEY_LAST_ADDRESS);
        if (!lastAddress) return null;
        const cached = window.localStorage.getItem(STORAGE_KEY_HAD_BALANCE_PREFIX + lastAddress);
        if (cached === "true") return true;
        if (cached === "false") return false;
        return null;
    } catch {
        return null;
    }
}

function writeCachedHadBalance(address: string, hadBalance: boolean): void {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(STORAGE_KEY_HAD_BALANCE_PREFIX + address, hadBalance ? "true" : "false");
        window.localStorage.setItem(STORAGE_KEY_LAST_ADDRESS, address);
    } catch {
        // localStorage may be disabled or quota-exceeded — ignore.
    }
}

interface UseHasBalanceResult {
    /** `true`/`false` when balances are resolved, `null` while indeterminate
     *  (grace window, wallet connecting, or balance fetch in flight). */
    hasBalance: boolean | null;
    /** Last definitive answer seen this session, seeded from per-wallet
     *  localStorage cache. Does not regress to `null` during mid-session
     *  refetches — callers use this for stable layout decisions. */
    lastKnown: boolean | null;
}

/**
 * Aggregated "does this wallet have any Solomon position" answer.
 *
 * Combines token balances (USDv / sUSDv) with pending unstake requests to
 * determine whether the dashboard or landing page should render. Returns
 * `null` while the answer is indeterminate — caller's responsibility to
 * render a loading shell during that window. The last definitive answer
 * is cached per-wallet in localStorage so future page loads can seed
 * `lastKnown` synchronously.
 */
export function useHasBalance(walletSettled: boolean): UseHasBalanceResult {
    const { connected, connecting, publicKey } = useWallet();
    const { balances, isLoading } = useTokenBalancesData();
    const { data: userStats } = useUserStakingStats(publicKey?.toString() ?? null);

    const hasPendingWithdrawals = (userStats?.unstakingQueue?.length ?? 0) > 0;

    const hasBalance = useMemo((): boolean | null => {
        if (!walletSettled) return null;
        if (connecting) return null;
        if (!connected) return false;
        if (isLoading) return null;

        return !isZero(balances.usdv) || !isZero(balances.susdv) || hasPendingWithdrawals;
    }, [walletSettled, connected, connecting, balances, isLoading, hasPendingWithdrawals]);

    // Retain the last definitive answer so mid-session refetches (which flip
    // `hasBalance` back to null briefly) don't regress the caller's shell.
    const lastKnownRef = useRef<boolean | null>(readCachedHadBalance());
    if (hasBalance !== null) {
        lastKnownRef.current = hasBalance;
    }

    useEffect(() => {
        if (hasBalance === null) return;
        if (!connected || !publicKey) return;
        writeCachedHadBalance(publicKey.toString(), hasBalance);
    }, [hasBalance, connected, publicKey]);

    return { hasBalance, lastKnown: lastKnownRef.current };
}
